import { useAuth } from "@/src/contexts/auth.context";
import { decodeToken } from "@/src/services/api/auth.api";
import {
  GetUserProfilesResponse,
  InsertUserProfileInput,
  InsertUserProfileResponse,
  UpdateUserProfileResponse,
  UserProfileItem,
} from "@/src/models/user.model";
import { NEWS_SAVED_QUERY_KEY } from "@/src/query-keys/news.keys";
import { POSTS_QUERY_KEY } from "@/src/query-keys/post.keys";
import { USER_PROFILE_QUERY_KEYS } from "@/src/query-keys/users.keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Toast from "react-native-toast-message";
import gqlClient from "../api/graphql.client";
import {
  GET_USER_GROUP_IDS,
  GET_USER_PROFILES,
  GET_USER_PROFILE_COUNTRIES,
  INSERT_USER_PROFILE,
  UPDATE_POST_SAVE_COUNT_MUTATION,
  CREATE_USER_PREFERENCES_MUTATION,
  GET_USER_PREFERENCES_QUERY,
  GET_ALL_USER_PROFILES,
  UPDATE_USER_PREFERENCES_MUTATION,
  UPDATE_USER_PROFILE,
  UPDATE_USER_SAVED_POSTS_MUTATION,
} from "./profile.query";
import { queryClient } from "@/src/config/react-query";

const useInvalidateUserProfiles = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEYS.base });
};

export const useGetUserProfileQuery = (userId: string) =>
  useQuery<UserProfileItem | null>({
    queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetUserProfilesResponse>(
        GET_USER_PROFILES,
        {
          filter: JSON.stringify({ CreatedBy: userId }),
          sort: "{}",
          pageNo: 1,
          pageSize: 1,
        },
      );
      return data.getuserprofiles.items[0] ?? null;
    },
    enabled: !!userId,
  });

export type UserCountriesData = {
  countryByUserId: Record<string, string>;
  countries: string[];
};

export const useGetUserCountries = (options?: { enabled?: boolean }) =>
  useQuery<UserCountriesData>({
    queryKey: [...USER_PROFILE_QUERY_KEYS.base, "countries"],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<{
        getuserprofiles: {
          items: { CreatedBy: string | null; country: string | null }[];
        };
      }>(GET_USER_PROFILE_COUNTRIES, {
        filter: "{}",
        sort: "{}",
        pageNo: 1,
        pageSize: 1000,
      });

      const countryByUserId: Record<string, string> = {};
      for (const item of data.getuserprofiles.items) {
        const country = item.country?.trim();
        if (item.CreatedBy && country) {
          countryByUserId[item.CreatedBy] = country;
        }
      }
      const countries = [...new Set(Object.values(countryByUserId))].sort(
        (a, b) => a.localeCompare(b),
      );
      return { countryByUserId, countries };
    },
    enabled: options?.enabled ?? true,
  });

export const useGetUserGroupIdsQuery = (
  userId: string,
  options?: { enabled?: boolean },
) =>
  useQuery<string[] | null>({
    queryKey: USER_PROFILE_QUERY_KEYS.groupIdsByUserId(userId),
    queryFn: async () => {
      const client = await gqlClient();

      const data = await client.request<{
        getuserprofiles: { items: { groupIds: string[] }[] };
      }>(GET_USER_GROUP_IDS, {
        filter: JSON.stringify({ CreatedBy: userId }),
      });

      return data.getuserprofiles.items[0]?.groupIds ?? null;
    },
    enabled: (options?.enabled ?? true) && !!userId,
  });
export const useGetUserPreferencesQuery = (userId: string) =>
  useQuery({
    queryKey: ["userPreferences", userId],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request(GET_USER_PREFERENCES_QUERY, {
        filter: JSON.stringify({ CreatedBy: userId }),
        sort: "{}",
      });
      return data.getuserprofiles.items[0]?.preferences ?? null;
    },
    enabled: !!userId,
  });

export const useUpdateUserPreferences = () => {
  return useMutation({
    mutationFn: async (variables: {
      filter: string;
      language?: string;
      lightMode?: boolean;
    }) => {
      const client = await gqlClient();
      return client.request(UPDATE_USER_PREFERENCES_MUTATION, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userPreferences"] });
    },
  });
};

export const useCreateUserPreferences = () => {
  return useMutation({
    mutationFn: async (variables: { language: string; lightMode: boolean }) => {
      const client = await gqlClient();
      return client.request(CREATE_USER_PREFERENCES_MUTATION, variables);
    },
  });
};

export const useInsertUserProfileMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async (input: InsertUserProfileInput) => {
      const client = await gqlClient();
      const data = await client.request<InsertUserProfileResponse>(
        INSERT_USER_PROFILE,
        input,
      );
      return data.insertuserprofile;
    },
    onSuccess: () => invalidate(),
  });
};

export const useUpdateUserProfileMutation = () => {
  const invalidate = useInvalidateUserProfiles();
  return useMutation({
    mutationFn: async ({
      input,
      userId,
    }: {
      input: InsertUserProfileInput;
      userId: string;
    }) => {
      const client = await gqlClient();
      const data = await client.request<UpdateUserProfileResponse>(
        UPDATE_USER_PROFILE,
        {
          filter: JSON.stringify({ CreatedBy: userId }),
          ...input,
        },
      );
      return data.updateuserprofile;
    },
    onSuccess: () => {
      invalidate();
    },
  });
};

export const useToggleSavePostMutation = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.itemId ?? "";

  return useMutation({
    mutationFn: async ({
      userProfileId,
      savedPostIds,
      postId,
      postSaveCount,
    }: {
      userProfileId: string;
      savedPostIds: string[];
      postId: string;
      postSaveCount: number;
    }) => {
      const client = await gqlClient();
      await client.request(UPDATE_USER_SAVED_POSTS_MUTATION, {
        filter: JSON.stringify({ ItemId: userProfileId }),
        savedPostIds,
      });

      const isSaving = savedPostIds.includes(postId);
      await client.request(UPDATE_POST_SAVE_COUNT_MUTATION, {
        filter: JSON.stringify({ ItemId: postId }),
        saveCount: isSaving
          ? (postSaveCount ?? 0) + 1
          : Math.max(0, (postSaveCount ?? 0) - 1),
      });
    },

    onMutate: async ({
      savedPostIds,
      postId,
      postSaveCount,
    }): Promise<{ previousProfile: unknown }> => {
      await queryClient.cancelQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });

      const previousProfile = queryClient.getQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
      );

      queryClient.setQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
        (old: any) => {
          if (!old) return old;
          return { ...old, savedPostIds };
        },
      );

      const isSaving = savedPostIds.includes(postId);
      // Optimistically remove post from saved list if unsaving
      if (!isSaving) {
        queryClient.setQueryData(
          [POSTS_QUERY_KEY, "saved", userId],
          (old: any) => {
            if (!old) return old;
            return old.filter((post: any) => post.ItemId !== postId);
          },
        );
      }
      queryClient.setQueryData([POSTS_QUERY_KEY, ""], (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            getposts: {
              ...page.getposts,
              items: page.getposts.items.map((post: any) => {
                if (post.ItemId !== postId) return post;
                return {
                  ...post,
                  saveCount: isSaving
                    ? (postSaveCount ?? 0) + 1
                    : Math.max(0, (postSaveCount ?? 0) - 1),
                };
              }),
            },
          })),
        };
      });

      queryClient.setQueryData([POSTS_QUERY_KEY, postId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          saveCount: isSaving
            ? (postSaveCount ?? 0) + 1
            : Math.max(0, (postSaveCount ?? 0) - 1),
        };
      });

      return { previousProfile };
    },

    onSuccess: (_data, { postId, savedPostIds }) => {
      const isSaving = savedPostIds.includes(postId);
      Toast.show({
        type: "success",
        text1: isSaving ? "SUCCESS_SAVE_POST" : "SUCCESS_UNSAVE_POST",
      });
    },

    onError: (
      _err,
      _variables,
      context: { previousProfile: unknown } | undefined,
    ) => {
      queryClient.setQueryData(
        USER_PROFILE_QUERY_KEYS.byUserId(userId),
        context?.previousProfile,
      );
      Toast.show({
        type: "error",
        text1: "ERROR_POST_SAVE_FAILED",
      });
    },

    onSettled: (_data, err) => {
      if (err) {
        queryClient.invalidateQueries({
          queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
        });
      }
      // Always invalidate saved posts query to sync the list
      queryClient.invalidateQueries({
        queryKey: [POSTS_QUERY_KEY, "saved", userId],
      });
    },
  });
};

const toggleId = (list: string[] = [], id: string): string[] =>
  list.includes(id) ? list.filter((i) => i !== id) : [...list, id];

export const useToggleSaved = (userId: string, type: "news" | "wiki") => {
  const queryClient = useQueryClient();
  const field = type === "news" ? "newsSaved" : "wikiSaved";
  const profileQueryKey = USER_PROFILE_QUERY_KEYS.byUserId(userId);

  return useMutation({
    mutationFn: async (_itemId: string) => {
      const currentProfile = queryClient.getQueryData<any>(profileQueryKey);
      const updated = Array.isArray(currentProfile?.[field])
        ? currentProfile[field]
        : [];

      const client = await gqlClient();
      await client.request(UPDATE_USER_PROFILE, {
        filter: JSON.stringify({ CreatedBy: userId }),
        [field]: updated,
      });
    },

    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: profileQueryKey });
      const previousProfile = queryClient.getQueryData(profileQueryKey);

      queryClient.setQueryData(profileQueryKey, (old: any) => {
        if (!old) return old;
        const current = Array.isArray(old[field]) ? old[field] : [];
        return { ...old, [field]: toggleId(current, itemId) };
      });

      return { previousProfile };
    },

    onError: (_err, _itemId, context) => {
      if (context?.previousProfile) {
        queryClient.setQueryData(profileQueryKey, context.previousProfile);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: profileQueryKey });
      queryClient.invalidateQueries({ queryKey: NEWS_SAVED_QUERY_KEY });
    },
  });
};

export const useToggleDraft = (userId: string, type: "news" | "wiki") => {
  const queryClient = useQueryClient();
  const { data: profile } = useGetUserProfileQuery(userId);

  return useMutation({
    mutationFn: async (itemId: string) => {
      const client = await gqlClient();

      const field = type === "news" ? "newsDraftIds" : "wikiDraftIds";

      const current = Array.isArray(profile?.[field]) ? profile[field] : [];

      const updated = toggleId(current, itemId);

      await client.request(UPDATE_USER_PROFILE, {
        filter: JSON.stringify({ CreatedBy: userId }),
        [field]: updated,
      });
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: USER_PROFILE_QUERY_KEYS.byUserId(userId),
      });
    },
  });
};

export const useGetAllUserProfilesQuery = () =>
  useQuery({
    queryKey: [...USER_PROFILE_QUERY_KEYS.base, "birthdays"],
    queryFn: async () => {
      const client = await gqlClient();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const next30Days = new Date(today);
      next30Days.setDate(today.getDate() + 30);

      const currentMonth = String(today.getMonth() + 1).padStart(2, "0");

      const orgId = (await decodeToken())?.org_id;
      const data = await client.request<GetUserProfilesResponse>(
        GET_ALL_USER_PROFILES,
        { month: `<${currentMonth}>`, orgId },
      );

      return data.getuserprofiles.items
        .filter((p) => {
          if (!p.dob) return false;
          const match = p.dob.match(/(\d+)\/<(\d+)>\/(\d+)/);
          if (!match) return false;
          const [, day, month] = match;
          const bday = new Date(
            today.getFullYear(),
            Number(month) - 1,
            Number(day),
          );
          if (bday < today) bday.setFullYear(today.getFullYear() + 1);
          return bday <= next30Days;
        })
        .slice(0, 5);
    },
    staleTime: 0,
  });
